/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable unicorn/no-useless-promise-resolve-reject */

/* eslint-disable unicorn/no-array-for-each */

/* eslint-disable unicorn/prevent-abbreviations */

import Axios, { type AxiosError, type AxiosInstance, type AxiosRequestConfig, type AxiosResponse } from 'axios'

import { Str } from '../Str'
import type { ClientHeaderResolver, ClientHeaders, ClientHttpMethod, ClientOptions, RequestOptions } from '.'
import { objectToFormdata } from './ObjectToFormdata'
import { Url } from './Url'

export class Client {
  public static isDebug = false

  protected isWithErrorResolver = true
  protected excludedHeaders: string[] = []

  protected $options: ClientOptions = {
    baseUrl: '',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    headerResolver: () => ({}),
    baseUrlResolver: () => this.$options.baseUrl,
    errorResolver: (error, _request) => {
      const logPrefix = '[NETWORK-RESPONSE]'
      Client.debug(`${logPrefix} ${JSON.stringify(error)} ${error}`)
      return Promise.reject(error)
    },
    responseResolver: (response) => Promise.resolve(response.data),
  }

  constructor(options: Partial<ClientOptions> = {}) {
    this.$options = { ...this.$options, ...options }
  }

  withoutErrorResolver(): this {
    this.isWithErrorResolver = false

    return this
  }

  withoutHeaders(headers: string | string[]): this {
    headers = Array.isArray(headers) ? headers : [headers]
    this.excludedHeaders = [...this.excludedHeaders, ...headers]

    return this
  }

  async call(
    method: ClientHttpMethod,
    url: string,
    payload: Record<string, any> | any | string,
    headers: ClientHeaders = {},
    options: RequestOptions = {},
  ): Promise<any> {
    const client = this.createAxios(method, url, payload, headers, options)
    const logRequestLine = `${method.toUpperCase()} ${decodeURIComponent(String(client.defaults.url))}`

    return client
      .request(client.defaults as any)
      .then(async (response: AxiosResponse) => {
        const logPrefix = '[NETWORK-RESPONSE]'
        Client.debug(`${logPrefix} HTTP ${response.status} ${logRequestLine}`)
        if (this.$options.responseResolver) {
          return this.$options.responseResolver(response)
        }

        return Promise.resolve(response)
      })
      .catch(async (error: AxiosError) => {
        if (error.response) {
          // The request was made and the server responded with a status code
          // that falls out of the range of 2xx
          // this is what we're used to
        } else if (error.request) {
          // The request was made but no response was received
          // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
          // http.ClientRequest in node.js
          Client.debug(error.request)
        } else {
          // Something happened in setting up the request that triggered an Error
          Client.debug('Error', error.message)
        }

        if (this.$options.errorResolver && this.isWithErrorResolver) {
          return this.$options.errorResolver(error, this, {
            method,
            url,
            payload,
            headers,
          })
        }

        return Promise.reject(error)
      })
      .finally(() => {
        this.isWithErrorResolver = true
      })
  }

  callGet(url: string, payload: object = {}, options: RequestOptions = {}): Promise<any> {
    return this.call('GET', url, payload, {}, options)
  }

  callPost(url: string, payload: any, options: RequestOptions = {}): Promise<any> {
    return this.call('POST', url, payload, {}, options)
  }

  callPatch(url: string, payload: any, options: RequestOptions = {}): Promise<any> {
    return this.call('PATCH', url, payload, {}, options)
  }

  callPut(url: string, payload: any, options: RequestOptions = {}): Promise<any> {
    return this.call('PUT', url, payload, {}, options)
  }

  callDelete(url: string, payload: string, options: RequestOptions = {}): Promise<any> {
    return this.call('DELETE', url, payload, {}, options)
  }

  getResolvedUrl(url = ''): string {
    if (Url.isValid(url)) {
      return url
    }

    let baseUrl = this.$options?.baseUrlResolver() || ''
    baseUrl = baseUrl ? Str.trim(baseUrl, '/') : ''

    return url ? `${baseUrl}/${Str.ltrim(url, '/')}` : baseUrl
  }

  getResolvedHeaders(headers: ClientHeaders = {}): any {
    const combinedHeaders = {
      ...this.$options.headers,
      ...this.$options.headerResolver(),
      ...headers,
    }

    // Filter any empty headers out of the request.
    Object.keys(combinedHeaders).forEach((headerKey) => {
      if (this.excludedHeaders.includes(headerKey)) {
        delete combinedHeaders[`${headerKey}`]
      }

      if (!combinedHeaders[`${headerKey}`]) {
        delete combinedHeaders[`${headerKey}`]
      }
    })

    return combinedHeaders
  }

  get baseUrl(): string {
    return this.$options.baseUrl || ''
  }

  set baseUrl(url: string) {
    this.$options.baseUrl = url
  }

  setHeaderResolver(resolver: ClientHeaderResolver): any {
    return {}
  }

  static debug(...messages: any[]): void {
    if (Client.isDebug) {
      messages.forEach((message) => {
        console.log(`(VUE-CORE) ${message}`)
      })
    }
  }

  protected payloadHasFile(payload: Record<string, any> | any | string): boolean {
    let hasFile = false
    const keys = Object.keys(payload)
    keys.forEach((property) => {
      if (
        (payload as any)[`${property}`] !== undefined &&
        (payload as any)[`${property}`] !== null &&
        (payload as any)[`${property}`].name !== undefined &&
        (payload as any)[`${property}`].lastModified !== undefined &&
        (payload as any)[`${property}`].size !== undefined
      ) {
        hasFile = true
      }
    })
    return hasFile
  }

  protected createAxios(
    method: ClientHttpMethod,
    url: string,
    payload: Record<string, any> | any | string,
    headers: ClientHeaders = {},
    requestOptions: RequestOptions,
  ): AxiosInstance {
    const options: AxiosRequestConfig = {}
    options.method = method
    options.url = this.getResolvedUrl(url)
    options.headers = this.getResolvedHeaders(headers)
    if (requestOptions.onDownloadProgress) {
      options.onDownloadProgress = requestOptions.onDownloadProgress
    }
    if (requestOptions.onUploadProgress) {
      options.onUploadProgress = requestOptions.onUploadProgress
    }

    if (['POST', 'PUT', 'PATCH'].includes(options.method)) {
      options.data = this.payloadHasFile(payload) ? objectToFormdata(payload) : payload
    }
    if (['GET', 'DELETE'].includes(options.method)) {
      options.params = payload
    }

    const logPrefix = '[NETWORK-REQUEST]'
    const logRequestLine = `${method.toUpperCase()} ${decodeURIComponent(options.url)}`
    Client.debug(`${logPrefix} ${logRequestLine}`)

    return Axios.create(options)
  }
}
